import axios from 'axios';

const E_CONSENT_SERVICE_URL = process.env.REACT_APP_E_CONSENT_SERVICE_URL;
const E_CONSENT_API_KEY = process.env.REACT_APP_E_CONSENT_API_KEY;

// Error code constants matching backend
const ERROR_CODES = {
    SUCCESS: "200",
    CREATED: "201",
    BAD_REQUEST: "400",
    UNAUTHORIZED: "401",
    FORBIDDEN: "403",
    NOT_FOUND: "404",
    CONFLICT: "409",
    GONE: "410",
    INTERNAL_ERROR: "500",
    SERVER_UNAVAILABLE: "503",
    TAXI_MAIL_ERROR: "5001"
};

// Error messages mapping
const ERROR_MESSAGES = {
    [ERROR_CODES.BAD_REQUEST]: "ข้อมูลที่ส่งมาไม่ถูกต้อง",
    [ERROR_CODES.UNAUTHORIZED]: "ไม่ได้รับอนุญาตให้เข้าถึง",
    [ERROR_CODES.FORBIDDEN]: "ไม่มีสิทธิ์เข้าถึงข้อมูล",
    [ERROR_CODES.NOT_FOUND]: "ไม่พบข้อมูลลูกค้า",
    [ERROR_CODES.GONE]: "ข้อมูลไม่พร้อมใช้งาน",
    [ERROR_CODES.INTERNAL_ERROR]: "เกิดข้อผิดพลาดภายในระบบ",
    [ERROR_CODES.SERVER_UNAVAILABLE]: "ระบบไม่พร้อมให้บริการ",
    [ERROR_CODES.TAXI_MAIL_ERROR]: "เกิดข้อผิดพลาดในการส่งอีเมล"
};

// Create custom error class
class ApiError extends Error {
    constructor(message, statusCode, errorCode) {
        super(message);
        this.name = 'ApiError';
        this.statusCode = statusCode;
        this.errorCode = errorCode;
    }
}

export const apiEbilling = {
    getConsentEBilling: async (customerId) => {
        if (!customerId) {
            throw new ApiError('กรุณาระบุรหัสลูกค้า', 400, ERROR_CODES.BAD_REQUEST);
        }
        try {
            const response = await axios.get(
                `${E_CONSENT_SERVICE_URL}api/v1/consent/customer/${customerId}`,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'apikey': `${E_CONSENT_API_KEY} `,
                    },
                }
            );

            // Handle successful response based on your example and transformation logic
            if (response.data.status_code === ERROR_CODES.SUCCESS) {
                if (response.data.data) {
                    // Transform the response to add 'success' property and wrap data
                    const customerData = response.data.data;

                    return {
                        success: true, // Add this flag for frontend consumption
                        data: {
                            custid: customerData.custid,
                            flagconsent: customerData.flagconsent,
                            email: customerData.email,
                            phoneNumber: customerData.mobile_phone,
                            updatedAt: customerData.updated_at,
                            firstName: customerData.first_name,
                            lastName: customerData.last_name
                        }
                    };
                } else {
                    // Case where status_code is SUCCESS but 'data' field is null/empty
                    return { success: true, data: null };
                }
            } else {
                // Handle error response with status_code from backend
                const errorCode = response.data.status_code;
                const errorMessage = response.data.error || ERROR_MESSAGES[errorCode] || 'เกิดข้อผิดพลาดที่ไม่ทราบสาเหตุ';
                throw new ApiError(errorMessage, response.status, errorCode);
            }
        } catch (error) {
            // Handle axios errors (network issues, non-2xx responses, etc.)
            if (error instanceof ApiError) {
                throw error; // Re-throw custom API errors
            }

            if (error.response) {
                // Server responded with error status (e.g., 400, 404, 500)
                const status = error.response.status;
                const responseData = error.response.data;

                let errorMessage = 'เกิดข้อผิดพลาดจากเซิร์ฟเวอร์';
                let errorCode = ERROR_CODES.INTERNAL_ERROR;

                if (responseData) {
                    if (responseData.status_code) {
                        errorCode = responseData.status_code;
                        errorMessage = responseData.error || ERROR_MESSAGES[errorCode] || errorMessage;
                    } else if (responseData.message) {
                        errorMessage = responseData.message;
                    } else if (responseData.error) {
                        errorMessage = responseData.error;
                    }
                }

                // Fallback to map HTTP status to error codes if backend doesn't provide status_code in responseData
                if (!responseData?.status_code) {
                    switch (status) {
                        case 400:
                            errorCode = ERROR_CODES.BAD_REQUEST;
                            break;
                        case 401:
                            errorCode = ERROR_CODES.UNAUTHORIZED;
                            break;
                        case 403:
                            errorCode = ERROR_CODES.FORBIDDEN;
                            break;
                        case 404:
                            errorCode = ERROR_CODES.NOT_FOUND;
                            break;
                        case 410:
                            errorCode = ERROR_CODES.GONE;
                            break;
                        case 500:
                            errorCode = ERROR_CODES.INTERNAL_ERROR;
                            break;
                        case 503:
                            errorCode = ERROR_CODES.SERVER_UNAVAILABLE;
                            break;
                        default:
                            errorCode = ERROR_CODES.INTERNAL_ERROR;
                    }
                    errorMessage = ERROR_MESSAGES[errorCode] || errorMessage;
                }

                throw new ApiError(errorMessage, status, errorCode);
            } else if (error.request) {
                // Request was made but no response received (e.g., network down, CORS issue)
                throw new ApiError(
                    'ไม่สามารถเชื่อมต่อกับเซิร์ฟเวอร์ กรุณาตรวจสอบการเชื่อมต่ออินเทอร์เน็ต',
                    0, // No HTTP status code received
                    ERROR_CODES.SERVER_UNAVAILABLE
                );
            } else {
                // Something else happened while setting up the request (e.g., malformed URL)
                throw new ApiError(
                    'เกิดข้อผิดพลาดในการส่งคำขอ: ' + error.message,
                    0,
                    ERROR_CODES.INTERNAL_ERROR
                );
            }
        }
    },
    createVerifyOTPEmail: async (customerInfo) => {
        try {
            const response = await axios.post(
                `${E_CONSENT_SERVICE_URL}api/v1/otp/create-otp`, // Ensure this is the correct endpoint for sending OTP
                {
                    customer_id: customerInfo.customer_id,
                    email: customerInfo.email,
                    phone_number: customerInfo.phoneNumber,
                    owner: customerInfo.owner,
                    first_name: customerInfo.firstName,
                    last_name: customerInfo.lastName,
                },
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'apikey': `${E_CONSENT_API_KEY} `, // Your API Key
                    },
                }
            );
            // This assumes your backend returns a success property directly or you handle it in the component
            // Based on image_035b04.png, it could return status_code 201 Created on success
            if (response.data.status_code === ERROR_CODES.SUCCESS) {
                return { success: true, data: response.data }; // Return a success flag and the full response data
            } else {
                // Handle cases where API returns non-success status_code but not an error HTTP status
                const errorCode = response.data.status_code;
                const errorMessage = response.data.error || ERROR_MESSAGES[errorCode] || 'Failed to send OTP.';
                throw new ApiError(errorMessage, response.status, errorCode);
            }

        } catch (error) {
            // Re-use ApiError for consistent error handling
            if (error instanceof ApiError) {
                throw error;
            }

            if (error.response) {
                const status = error.response.status;
                const responseData = error.response.data;

                let errorMessage = 'เกิดข้อผิดพลาดในการส่ง OTP';
                let errorCode = ERROR_CODES.INTERNAL_ERROR;

                if (responseData) {
                    if (responseData.status_code) {
                        errorCode = responseData.status_code;
                        errorMessage = responseData.error || ERROR_MESSAGES[errorCode] || errorMessage;
                    } else if (responseData.message) {
                        errorMessage = responseData.message;
                    } else if (responseData.error) {
                        errorMessage = responseData.error;
                    }
                }

                if (!responseData?.status_code) { // Fallback for HTTP status mapping
                    switch (status) {
                        case 400: errorCode = ERROR_CODES.BAD_REQUEST; break;
                        case 401: errorCode = ERROR_CODES.UNAUTHORIZED; break;
                        case 403: errorCode = ERROR_CODES.FORBIDDEN; break;
                        case 404: errorCode = ERROR_CODES.NOT_FOUND; break;
                        case 500: errorCode = ERROR_CODES.INTERNAL_ERROR; break;
                        default: errorCode = ERROR_CODES.INTERNAL_ERROR;
                    }
                    errorMessage = ERROR_MESSAGES[errorCode] || errorMessage;
                }
                throw new ApiError(errorMessage, status, errorCode);
            } else if (error.request) {
                throw new ApiError(
                    'ไม่สามารถเชื่อมต่อกับเซิร์ฟเวอร์เพื่อส่ง OTP',
                    0,
                    ERROR_CODES.SERVER_UNAVAILABLE
                );
            } else {
                throw new ApiError(
                    'เกิดข้อผิดพลาดในการส่งคำขอ OTP: ' + error.message,
                    0,
                    ERROR_CODES.INTERNAL_ERROR
                );
            }
        }
    },
    checkOTP: async (payload) => {
        try {
            const response = await axios.post(
                `${E_CONSENT_SERVICE_URL}api/v1/otp/check-otp`,
                {
                    id: payload.id,
                    phone_number: payload.phone_number || "", // Ensure empty string if undefined
                    email: payload.email,
                    otp: payload.otp, // ← Fixed: use 'otp' not 'otpCode'
                    otp_ref: payload.otp_ref,
                    expire_time: payload.expire_time,
                    create_epoch: Number(payload.create_epoch) || 0, // ← Ensure it's a number
                    owner: payload.owner || "",
                    customer_id: payload.customer_id || "",
                },
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'apikey': `${E_CONSENT_API_KEY} `,
                    },
                }
            );

            // Assuming 200 SUCCESS means valid OTP
            if (response.data.status_code === ERROR_CODES.SUCCESS) {
                return { success: true, data: response.data.data };
            } else {
                const errorCode = response.data.status_code;
                const errorMessage = response.data.error || ERROR_MESSAGES[errorCode] || 'รหัส OTP ไม่ถูกต้อง';
                throw new ApiError(errorMessage, response.status, errorCode);
            }
        } catch (error) {
            if (error instanceof ApiError) {
                throw error;
            }

            if (error.response) {
                const status = error.response.status;
                const responseData = error.response.data;

                let errorMessage = 'เกิดข้อผิดพลาดในการยืนยัน OTP';
                let errorCode = ERROR_CODES.INTERNAL_ERROR;

                if (responseData) {
                    if (responseData.status_code) {
                        errorCode = responseData.status_code;
                        errorMessage = responseData.error || ERROR_MESSAGES[errorCode] || errorMessage;
                    } else if (responseData.message) {
                        errorMessage = responseData.message;
                    } else if (responseData.error) {
                        errorMessage = responseData.error;
                    }
                }
                if (!responseData?.status_code) {
                    switch (status) {
                        case 400: errorCode = ERROR_CODES.BAD_REQUEST; break;
                        case 401: errorCode = ERROR_CODES.UNAUTHORIZED; break;
                        case 403: errorCode = ERROR_CODES.FORBIDDEN; break;
                        case 404: errorCode = ERROR_CODES.NOT_FOUND; break;
                        case 500: errorCode = ERROR_CODES.INTERNAL_ERROR; break;
                        default: errorCode = ERROR_CODES.INTERNAL_ERROR;
                    }
                    errorMessage = ERROR_MESSAGES[errorCode] || errorMessage;
                }
                throw new ApiError(errorMessage, status, errorCode);
            } else if (error.request) {
                throw new ApiError(
                    'ไม่สามารถเชื่อมต่อกับเซิร์ฟเวอร์เพื่อยืนยัน OTP',
                    0,
                    ERROR_CODES.SERVER_UNAVAILABLE
                );
            } else {
                throw new ApiError(
                    'OTP Invalid: ' + error.message,
                    0,
                    ERROR_CODES.INTERNAL_ERROR
                );
            }
        }
    },
    createConsentEBilling: async (customerInfo) => {
        try {
            const response = await axios.post(
                `${E_CONSENT_SERVICE_URL}api/v1/consent/create-ebilling`,
                {
                    cust_id: customerInfo.customerId,
                    flag_consent: "N",
                    email: customerInfo.email,
                    first_name: customerInfo.firstName,
                    last_name: customerInfo.lastName,
                    flag_verify_email: "N",
                },
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'apikey': `${E_CONSENT_API_KEY} `,
                    },
                }
            );

            // Check for both SUCCESS and CREATED status codes
            if (response.data.status_code === ERROR_CODES.SUCCESS || response.data.status_code === ERROR_CODES.CREATED) {
                return { success: true, data: response.data };
            } else {
                const errorCode = response.data.status_code;
                const errorMessage = response.data.error || ERROR_MESSAGES[errorCode] || 'Failed to create consent e-billing.';
                throw new ApiError(errorMessage, response.status, errorCode);
            }

        } catch (error) {
            if (error instanceof ApiError) {
                throw error;
            }

            if (error.response) {
                const status = error.response.status;
                const responseData = error.response.data;
                let errorMessage = responseData?.error || responseData?.message || 'เกิดข้อผิดพลาดภายในระบบ';
                let errorCode = responseData?.status_code || ERROR_CODES.INTERNAL_ERROR;

                if (!responseData?.status_code) {
                    switch (status) {
                        case 400: errorCode = ERROR_CODES.BAD_REQUEST; break;
                        case 401: errorCode = ERROR_CODES.UNAUTHORIZED; break;
                        case 409: errorCode = ERROR_CODES.CONFLICT; break;
                        case 500: errorCode = ERROR_CODES.INTERNAL_ERROR; break;
                        default: errorCode = ERROR_CODES.INTERNAL_ERROR;
                    }
                    errorMessage = ERROR_MESSAGES[errorCode] || errorMessage;
                }
                throw new ApiError(errorMessage, status, errorCode);
            } else if (error.request) {
                throw new ApiError(
                    'ไม่สามารถเชื่อมต่อกับเซิร์ฟเวอร์เพื่อสร้าง e-billing',
                    0,
                    ERROR_CODES.SERVER_UNAVAILABLE
                );
            } else {
                throw new ApiError(
                    'เกิดข้อผิดพลาดในการส่งคำขอสร้าง e-billing: ' + error.message,
                    0,
                    ERROR_CODES.INTERNAL_ERROR
                );
            }
        }
    },
    updateConsentEBilling: async (customerInfo) => {
        try {
            const response = await axios.post(
                `${E_CONSENT_SERVICE_URL}api/v1/consent/update-ebilling/${customerInfo.customerId}`,
                {
                    email: customerInfo.email,
                },
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'apikey': `${E_CONSENT_API_KEY} `,
                    },
                }
            );

            // Check for both SUCCESS and CREATED status codes
            if (response.data.status_code === ERROR_CODES.SUCCESS) {
                return { success: true, data: response.data };
            } else {
                const errorCode = response.data.status_code;
                const errorMessage = response.data.error || ERROR_MESSAGES[errorCode] || 'Failed to create consent e-billing.';
                throw new ApiError(errorMessage, response.status, errorCode);
            }

        } catch (error) {
            if (error instanceof ApiError) {
                throw error;
            }

            if (error.response) {
                const status = error.response.status;
                const responseData = error.response.data;
                let errorMessage = responseData?.error || responseData?.message || 'เกิดข้อผิดพลาดภายในระบบ';
                let errorCode = responseData?.status_code || ERROR_CODES.INTERNAL_ERROR;

                if (!responseData?.status_code) {
                    switch (status) {
                        case 400: errorCode = ERROR_CODES.BAD_REQUEST; break;
                        case 401: errorCode = ERROR_CODES.UNAUTHORIZED; break;
                        case 500: errorCode = ERROR_CODES.INTERNAL_ERROR; break;
                        default: errorCode = ERROR_CODES.INTERNAL_ERROR;
                    }
                    errorMessage = ERROR_MESSAGES[errorCode] || errorMessage;
                }
                throw new ApiError(errorMessage, status, errorCode);
            } else if (error.request) {
                throw new ApiError(
                    'ไม่สามารถเชื่อมต่อกับเซิร์ฟเวอร์เพื่อสร้าง e-billing',
                    0,
                    ERROR_CODES.SERVER_UNAVAILABLE
                );
            } else {
                throw new ApiError(
                    'เกิดข้อผิดพลาดในการส่งคำขอแก้ไข e-billing: ' + error.message,
                    0,
                    ERROR_CODES.INTERNAL_ERROR
                );
            }
        }
    },

};

export { ERROR_CODES, ERROR_MESSAGES, ApiError };